import { useRef } from 'react'
import { Box, Button, Flex, HStack, Input, Text, useDisclosure } from '@chakra-ui/react'
import CategoryFileListModal from './CategoryFileListModal'

// Renders ONE category's file list per the shared display rule: 0 files -> a
// placeholder (or an upload control when editable), exactly 1 file -> inline
// View/Download (plus inline Add/Remove when editable, no modal needed),
// 2+ files -> a Detail button opening a modal with the full list. Iterating
// over a list of categories, and any category-name editing, is left to the
// host app (this package has no react-hook-form/Redux/data-fetching).
const CategoryFileList = ({
  files = [],
  editable = false,
  onView,
  onDownload,
  onAddFiles,
  onRemoveFile,
  allowedFileTypes,
  title,
  emptyText = '(No File Uploaded)',
  detailButtonText = 'Detail',
}) => {
  const fileInputRef = useRef(null)
  const { isOpen, onOpen, onClose } = useDisclosure()

  const handleFileInputChange = (e) => {
    if (e.target.files?.length > 0 && onAddFiles) {
      onAddFiles(e.target.files)
    }
    e.target.value = ''
  }

  const triggerFileInput = () => fileInputRef.current?.click()

  if (files.length === 0) {
    if (!editable) {
      return <Text>{emptyText}</Text>
    }
    return (
      <Box>
        <Input type="file" accept={allowedFileTypes} multiple onChange={handleFileInputChange} />
      </Box>
    )
  }

  if (files.length === 1) {
    const file = files[0]
    return (
      <HStack gap={2}>
        <Button type="button" colorScheme="blue" onClick={() => onView?.(file)}>
          View
        </Button>
        <Button type="button" colorScheme="green" onClick={() => onDownload?.(file)}>
          Download
        </Button>
        {editable && (
          <>
            <Button type="button" size="sm" onClick={triggerFileInput}>
              + Add File
            </Button>
            <Button type="button" size="sm" colorScheme="red" onClick={() => onRemoveFile?.(file)}>
              Remove
            </Button>
            <Input
              ref={fileInputRef}
              type="file"
              accept={allowedFileTypes}
              multiple
              display="none"
              onChange={handleFileInputChange}
            />
          </>
        )}
      </HStack>
    )
  }

  return (
    <>
      <Flex gap={2} alignItems="center">
        <Button type="button" colorScheme="blue" onClick={onOpen}>
          {detailButtonText}
        </Button>
        <Text overflowX="hidden" textOverflow="ellipsis" whiteSpace="nowrap" maxW="250px">
          {files.map((file) => file.originalFileName).join(', ')}
        </Text>
      </Flex>
      <CategoryFileListModal
        isOpen={isOpen}
        onClose={onClose}
        title={title}
        files={files}
        editable={editable}
        onView={onView}
        onDownload={onDownload}
        onRemoveFile={onRemoveFile}
        onAddFiles={onAddFiles}
        allowedFileTypes={allowedFileTypes}
      />
    </>
  )
}

export default CategoryFileList
